import { apiFetcher, apiFetcherAi } from "../services/API.service";
import { AI_CHAT_LOCAL_STORAGE_KEY } from "../utilities/constants";
import { validateAIUserDataResponse } from "../utilities/responseValidators";
import { retrieveDeviceId } from "../utilities/utils";
export async function getUpdatedUserData(): Promise<ValidateUserDataResponse | null> {
  try {
    const response = await apiFetcher({
      route: "/profile/chat/details",
      method: "POST",
      options: {
        body: {}
      }
    });

    if (response.status === 200 && response.data) {
      const validData = validateAIUserDataResponse(response.data as AiUserData);

      if (validData) {
        return validData;
      }
      return null;
    } else {
      console.error("Error fetching user data for AI Chat");
      return null;
    }
  } catch (error) {
    console.error("Error fetching updated user data:", error);
    return null;
  }
}

export const sendMessagesAiChat = async (
  systemMessage: string | null,
  userMessage: string,
  currentPage: string,
  signal?: AbortSignal
): Promise<
  | {
      rStatus: "success";
      message: string;
    }
  | { rStatus: "error"; message: string; messageKey: StatusKeyVals }
> => {
  const no_response_message = "no_response_message";
  const error_response_message = "error_response_message";
  const empty_user_message = "empty_user_message";
  const chat_no_network_error = "chat_no_network_error";

  //1) Validate Messages
  if (
    !userMessage ||
    typeof userMessage !== "string" ||
    userMessage.trim() === ""
  ) {
    console.error(
      "Error validating user message, no message provided or incorrect type"
    );
    return {
      rStatus: "error",
      message: empty_user_message,
      messageKey: "missing-data"
    };
  }

  if (
    systemMessage !== null &&
    (typeof systemMessage !== "string" || systemMessage.trim() === "")
  ) {
    console.error(
      "Error validating system message, incorrect type or empty string provided"
    );
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "missing-data"
    };
  }

  //
  // 2) Get the User Data
  const chatData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);
  let userData: AiUserData | null = null;
  if (!chatData) {
    console.error(
      `Cannot send message, no ${AI_CHAT_LOCAL_STORAGE_KEY} in local storage`
    );
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "missing-data"
    };
  } else {
    try {
      const lsData = JSON.parse(chatData);
      userData = lsData.user_data;
      if (!userData || typeof userData !== "object") {
        console.error(
          "Missing user data in local storage or incorrect type. Full Data:",
          lsData
        );
        return {
          rStatus: "error",
          message: error_response_message,
          messageKey: "missing-data"
        };
      }
    } catch (error) {
      console.error("Error parsing user data", error);
      return {
        rStatus: "error",
        message: error_response_message,
        messageKey: "incorrect_data_format"
      };
    }
  }
  // validate the data
  const response = validateAIUserDataResponse(userData);
  if (!response) {
    console.error("Error validating user data, no validation response");
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "incorrect_data_format"
    };
  }

  // 3) Check the Page Name
  if (!currentPage || typeof currentPage !== "string") {
    console.error("Error validating page name, no page name provided");
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "missing-data"
    };
  }

  //4) Validate / Generate device ID
  // Note - Generation is added here as a precaution, however it should be generated by the
  // socket controller
  let deviceID = retrieveDeviceId();
  if (
    !deviceID ||
    deviceID === null ||
    typeof deviceID !== "string" ||
    deviceID.trim() === ""
  ) {
    console.error(
      "Error generating or retrieving device for AI Message send, cannot send message."
    );
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "missing-data"
    };
  }

  // 5) add deviceID to userData
  const fullUserData: AIUserDataMessageSend = {
    ...userData,
    device_id: deviceID
  };

  try {
    const sendResponse = await apiFetcherAi({
      route: "/goalrush-chat",
      method: "POST",
      options: {
        body: {
          message: userMessage,
          page: `/${currentPage}`,
          userData: fullUserData,
          systemMessage: systemMessage
        }
      },
      signal: signal
    });

    if (sendResponse && sendResponse.statusKey) {
      console.log("SEND RESPONSE", sendResponse);
      if (sendResponse.statusKey === "success") {
        console.log("Response from AI", sendResponse.data);
        if (sendResponse.data) {
          const aiResponse = sendResponse.data;
          if (!aiResponse || typeof aiResponse !== "string") {
            console.error("Error validating AI response, no response received");
            return {
              rStatus: "error",
              message: no_response_message,
              messageKey: "no_response"
            };
          } else {
            return {
              rStatus: "success",
              message: aiResponse
            };
          }
        } else {
          console.error("AI Chat - success response but no data received");
          return {
            rStatus: "error",
            message: no_response_message,
            messageKey: "no_data"
          };
        }
      } else if (sendResponse.statusKey === "error_network") {
        // No network
        return {
          rStatus: "error",
          message: chat_no_network_error,
          messageKey: "no_data"
        };
      } else {
        return {
          rStatus: "error",
          message: error_response_message,
          messageKey: sendResponse.statusKey
        };
      }
    } else {
      console.error("Error sending AI message, no response received");
      return {
        rStatus: "error",
        message: no_response_message,
        messageKey: "termination_unknown_handler"
      };
    }
  } catch (error) {
    console.log("Error sending AI message", error);
    return {
      rStatus: "error",
      message: error_response_message,
      messageKey: "incorrect_data_format"
    };
  }
};

// ============== TEST FUNCTIONS ==================================================================

// export function aiLocalStoragePopulate(): void {
//   const data = {
//     user_data: {
//       uuid: "123456",
//       timestamp: Math.floor(Date.now() / 1000), // Convert to epoch
//       nickname: "ben",
//       bidsPlaced: 1,
//       referrals: 4,
//       bidsBought: true
//     },
//     page_data: {
//       home: [
//         {
//           page_ref: "home-1",
//           complete: true,
//           question: {
//             content: "How did you find out about us?",
//             timestamp: Math.floor(Date.now() / 1000) // Convert to epoch
//           },
//           answer: {
//             content: "Google Search",
//             timestamp: Math.floor(Date.now() / 1000)
//           } // Convert to epoch
//         },
//         {
//           page_ref: "home-2",
//           complete: false,
//           question: { content: "What items would you bid on" },
//           timestamp: Math.floor(Date.now() / 1000), // Convert to epoch
//           answer: {
//             content: "Games and a big green bicycle",
//             timestamp: Math.floor(Date.now() / 1000) // Convert to epoch
//           }
//         }
//       ],
//       login: [{ page_ref: "login-1" }],
//       signup: [{ page_ref: "signup-1" }]
//     }
//   };

//   localStorage.setItem("chat", JSON.stringify(data));
// }
